import { Fragment, isValidElement, memo } from 'react';

import { cn } from '../../lib/styles';
import { PageColor } from '../../types';
import { EllipsisTooltip } from '../EllipsisTooltip';

const LABELED_SECTION_HEADER_CLASS_BY_COLOR: Record<PageColor, string> = {
  gray: 'border-border-default bg-gray-2',
  red: 'border-red-2/80 bg-red-2/50',
  green: 'border-green-2 bg-green-2/80',
  teal: 'border-teal-2 bg-teal-2/80',
  blue: 'border-blue-2 bg-blue-2',
  orange: 'border-orange-4 bg-orange-5/30',
  yellow: 'border-yellow-3 bg-yellow-2',
  violet: 'border-violet-2/20 bg-violet-1',
};

function isStringLike(node: React.ReactNode): boolean {
  return typeof node === 'string' || (isValidElement(node) && node.type === Fragment);
}

interface LabeledSectionProps {
  label: React.ReactNode;
  action?: React.ReactNode;
  color?: PageColor;
  children: React.ReactNode;
  className?: string;
}

const LabeledSectionBase = ({ label, color = 'gray', children, action, className }: LabeledSectionProps) => {
  return (
    <div
      className={cn(
        'group relative mt-4 flex flex-col',
        '[&_.header-action]:mt-0.5 [&_.header-action]:pointer-events-none [&_.header-action]:opacity-0 [&_.header-action]:transition-all [&_.header-action]:duration-150 [&_.header-action]:ease-in-out',
        className,
      )}
    >
      <div
        className={cn(
          'labeled-section__header absolute top-[-18px] left-0 z-1 flex h-12 w-full items-start justify-between gap-2 rounded-[10px] border border-solid px-4 pt-1 pb-4',
          LABELED_SECTION_HEADER_CLASS_BY_COLOR[color],
          'hover:[&_.header-action]:pointer-events-auto hover:[&_.header-action]:opacity-100',
        )}
      >
        <div className="min-w-0 flex-1">
          {isStringLike(label) ? (
            <div className="text-xs font-semibold text-text-secondary">
              <EllipsisTooltip>{label}</EllipsisTooltip>
            </div>
          ) : (
            label
          )}
        </div>

        {action}
      </div>

      <div className="relative z-2 mt-2.5 overflow-auto rounded-[10px] border border-border-default bg-gray-1 p-4">
        {children}
      </div>
    </div>
  );
};

export const LabeledSection = memo(LabeledSectionBase);
